/**
 * Admin quote queue page (P16-T10 step 2). Mirrors OrdersList layout +
 * filter ergonomics; renders the paginated `GET /v1/admin/quotes` queue.
 *
 * Filters (per spec §9.2):
 *   - status     multi-select; default {requested, revised}
 *   - companyId  UUID input (search-select TBD — fits the future
 *                 Combobox primitive; for now a plain text input)
 *   - ageGteDays slider 0..30
 *   - q          number + admin_note ILIKE
 *
 * Sort: server-side default (`AdminQuotesService.list`) — requested
 * oldest-first, then revised soonest-expiring, then everything else by
 * recency. The list does NOT re-sort client-side.
 *
 * Pagination: page/pageSize per the api shape (NOT cursor — admin queue
 * UX is fixed-page for printable handoff). 50 rows per page.
 *
 * Columns: Number, Status badge, Company (id-short), Requester
 * (id-short), Lines, Gross total, Age (days since requested_at),
 * Valid until (countdown chip if `revised`), Open action.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Numeric,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../../lib/api-client.js";
import { formatGBP } from "../../lib/format.js";
import { useToast } from "../../lib/toast.js";
import type { QuoteListItem, QuoteStatus } from "@wmart/shared-types";
import type { AdminQuoteListResponse } from "../../types/admin-quotes.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALL_STATUSES: QuoteStatus[] = [
  "requested",
  "revised",
  "accepted",
  "rejected",
  "cancelled",
  "expired",
];

const DEFAULT_STATUSES: QuoteStatus[] = ["requested", "revised"];

/**
 * Status tone mapping for admin quote pills.
 *
 * Picked to match the admin operator's "who needs me?" hierarchy:
 *   - requested  → warning (yellow) — needs admin action
 *   - revised    → info (blue)      — awaiting customer
 *   - accepted   → success (green)  — order placed
 *   - rejected   → neutral (grey)   — terminal, no further action
 *   - cancelled  → neutral (grey)   — terminal
 *   - expired    → neutral (grey)   — terminal
 *
 * Storefront uses a parallel mapping in `QuoteStatusBadge.tsx` (T11).
 */
const STATUS_TONE: Record<QuoteStatus, Tone> = {
  requested: "warning",
  revised: "info",
  accepted: "success",
  rejected: "neutral",
  cancelled: "neutral",
  expired: "neutral",
};

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Helpers — pure
// ---------------------------------------------------------------------------

/**
 * Days elapsed between `requestedAt` and `now`. Floors to integer days
 * (matches the "age_gte_days" slider semantics + the api-side
 * `ageGteDays` filter which uses INTERVAL day math).
 */
function ageDays(requestedAtIso: string, now: Date = new Date()): number {
  const requested = new Date(requestedAtIso).getTime();
  if (Number.isNaN(requested)) return 0;
  const ms = now.getTime() - requested;
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

/**
 * Countdown label for `valid_until` on revised quotes — "expires in 3d",
 * "expires in 4h", or "expired" if already past. Designed for the queue
 * chip — single-line, terse. Null `validUntil` returns null (caller
 * should render an em-dash).
 */
export function formatValidUntilChip(
  validUntilIso: string | null,
  now: Date = new Date(),
): string | null {
  if (!validUntilIso) return null;
  const validUntil = new Date(validUntilIso).getTime();
  if (Number.isNaN(validUntil)) return null;
  const ms = validUntil - now.getTime();
  if (ms <= 0) return "expired";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 1) return "<1h";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QuoteList() {
  const { notify, notifyProblem } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Filter state (seeded from URL for deep-link support) ----
  const [statusFilter, setStatusFilter] = useState<QuoteStatus[]>(() => {
    const fromUrl = searchParams.getAll("status");
    const valid = fromUrl.filter((s): s is QuoteStatus =>
      ALL_STATUSES.includes(s as QuoteStatus),
    );
    return valid.length > 0 ? valid : DEFAULT_STATUSES;
  });
  const [companyIdFilter, setCompanyIdFilter] = useState<string>(
    searchParams.get("companyId") ?? "",
  );
  const [ageGteDays, setAgeGteDays] = useState<number>(() => {
    const raw = searchParams.get("ageGteDays");
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n >= 0 && n <= 30 ? n : 0;
  });
  const [q, setQ] = useState<string>(searchParams.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState<string>(q);
  const [page, setPage] = useState<number>(() => {
    const raw = searchParams.get("page");
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  });

  // ---- Fetched data ----
  const [items, setItems] = useState<QuoteListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // Debounce free-text query (250ms — matches orders / customers).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 whenever filters change (not when paginating).
  // ageGteDays is a "live" slider — every change resets the page.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, companyIdFilter, ageGteDays, debouncedQ]);

  // ---- Fetch ----
  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      for (const s of statusFilter) params.append("status", s);
      if (companyIdFilter.trim() !== "") {
        params.set("companyId", companyIdFilter.trim());
      }
      if (ageGteDays > 0) {
        params.set("ageGteDays", String(ageGteDays));
      }
      if (debouncedQ.trim() !== "") {
        params.set("q", debouncedQ.trim());
      }
      params.set("page", String(page));
      params.set("pageSize", String(PAGE_SIZE));

      const data = await api.get<AdminQuoteListResponse>(
        `/v1/admin/quotes?${params.toString()}`,
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    companyIdFilter,
    ageGteDays,
    debouncedQ,
    page,
    notify,
    notifyProblem,
  ]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  // Sync URL with filters (replace so back-button doesn't accumulate).
  useEffect(() => {
    const next = new URLSearchParams();
    for (const s of statusFilter) next.append("status", s);
    if (companyIdFilter.trim() !== "") {
      next.set("companyId", companyIdFilter.trim());
    }
    if (ageGteDays > 0) next.set("ageGteDays", String(ageGteDays));
    if (debouncedQ.trim() !== "") next.set("q", debouncedQ.trim());
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, companyIdFilter, ageGteDays, debouncedQ, page]);

  const hasAnyFilter = useMemo(() => {
    if (companyIdFilter.trim() !== "") return true;
    if (ageGteDays > 0) return true;
    if (debouncedQ.trim() !== "") return true;
    // "Default" = exactly the 2 defaults in any order.
    if (statusFilter.length !== DEFAULT_STATUSES.length) return true;
    const set = new Set(statusFilter);
    return !DEFAULT_STATUSES.every((s) => set.has(s));
  }, [statusFilter, companyIdFilter, ageGteDays, debouncedQ]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-6">
      {/* Filters bar */}
      <div className="flex flex-wrap items-end gap-4">
        <fieldset className="flex flex-col gap-1">
          <legend className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
            Status
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {ALL_STATUSES.map((s) => {
              const active = statusFilter.includes(s);
              return (
                <Button
                  key={s}
                  variant={active ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => {
                    setStatusFilter((prev) =>
                      prev.includes(s)
                        ? prev.filter((p) => p !== s)
                        : [...prev, s],
                    );
                  }}
                  aria-pressed={active}
                >
                  {s}
                </Button>
              );
            })}
          </div>
        </fieldset>

        <div className="w-56">
          <Input
            type="search"
            label="Search"
            placeholder="Number or note…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="w-72">
          <Input
            type="text"
            label="Company ID"
            placeholder="UUID"
            value={companyIdFilter}
            onChange={(e) => setCompanyIdFilter(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="age-gte-days"
            className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500"
          >
            Age ≥ {ageGteDays}d
          </label>
          <input
            id="age-gte-days"
            type="range"
            min={0}
            max={30}
            step={1}
            value={ageGteDays}
            onChange={(e) => setAgeGteDays(Number(e.target.value))}
            className="w-48"
          />
        </div>

        {hasAnyFilter ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setStatusFilter(DEFAULT_STATUSES);
              setCompanyIdFilter("");
              setAgeGteDays(0);
              setQ("");
            }}
          >
            Reset
          </Button>
        ) : null}
      </div>

      {/* Empty / table */}
      {items.length === 0 && !loading ? (
        <EmptyState
          icon="fileText"
          title="No quotes match"
          body="Adjust filters above to surface more quotes."
        />
      ) : (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Number</TH>
                <TH>Status</TH>
                <TH>Company</TH>
                <TH>Requester</TH>
                <TH className="text-right">Lines</TH>
                <TH className="text-right">Total</TH>
                <TH className="text-right">Age</TH>
                <TH>Valid until</TH>
                <TH>{/* Open */}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((it) => {
                const chip = formatValidUntilChip(it.valid_until);
                return (
                  <TR key={it.id}>
                    <TD className="font-mono text-body-sm">
                      <Link
                        to={`/quotes/${it.id}`}
                        className="text-ink-900 hover:underline"
                      >
                        {it.number}
                      </Link>
                    </TD>
                    <TD>
                      <StatusPill tone={STATUS_TONE[it.status]}>
                        {it.status}
                      </StatusPill>
                    </TD>
                    <TD className="font-mono text-body-sm text-ink-700">
                      {shortId(it.company_id)}
                    </TD>
                    <TD className="font-mono text-body-sm text-ink-700">
                      {shortId(it.requester_customer_id)}
                    </TD>
                    <TD className="text-right">
                      <Numeric>{it.line_count}</Numeric>
                    </TD>
                    <TD className="text-right">
                      <Numeric serif>
                        {formatGBP(it.total_gross_minor)}
                      </Numeric>
                    </TD>
                    <TD className="text-right">
                      <Numeric>{ageDays(it.requested_at)}d</Numeric>
                    </TD>
                    <TD>
                      {chip ? (
                        <span
                          className={
                            chip === "expired"
                              ? "text-body-sm text-danger"
                              : "text-body-sm text-ink-700"
                          }
                        >
                          {chip}
                        </span>
                      ) : (
                        <span className="text-body-sm text-ink-500">—</span>
                      )}
                    </TD>
                    <TD>
                      <Link
                        to={`/quotes/${it.id}`}
                        className="text-body-sm text-accent hover:underline"
                      >
                        Open
                      </Link>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 text-body-sm">
              <span className="text-ink-500">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
